/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import React from 'react';
import { Button, Portal, Tooltip, VisuallyHidden } from '@chakra-ui/react';

interface FieldHelpTooltipProps {
  ariaLabel: string;
  content: React.ReactNode;
  buttonSize?: '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  buttonBoxSize?: string;
}

/**
 * **The `?` beside a label** — what a control is for, on request (`X28b` as amended, `#1276`).
 *
 * **The text is also in the accessible tree, bound to the `?` with `aria-describedby`** (`3c-ii`,
 * `ST12` item 1). A tooltip renders its content only while open, so help moved off the surface into
 * one was reachable by a mouse and by nothing else — the case *a use verdict is made at a screen*
 * names. `ControlHover` already did this for a refusal; the `?` now does it for help.
 */
export const FieldHelpTooltip: React.FC<FieldHelpTooltipProps> = ({
  ariaLabel,
  content,
  buttonSize = 'xs',
  buttonBoxSize = '12px',
}) => {
  const id = React.useId();
  return (
    <>
      <Tooltip.Root openDelay={120} closeDelay={220} interactive>
        <Tooltip.Trigger asChild>
          <Button
            size={buttonSize}
            variant="solid"
            minW={buttonBoxSize}
            h={buttonBoxSize}
            p={0}
            borderRadius="full"
            bg="accent.solid"
            color="white"
            aria-label={ariaLabel}
            aria-describedby={id}
            _hover={{ bg: 'accent.ink' }}
            _focusVisible={{
              outlineWidth: '2px',
              outlineStyle: 'solid',
              outlineColor: 'accent.solid',
              outlineOffset: '2px',
            }}
            fontSize="9px"
            fontWeight="700"
            lineHeight={1}
            transform="translateY(-0.25px)"
          >
            ?
          </Button>
        </Tooltip.Trigger>
        <Portal>
          <Tooltip.Positioner>
            <Tooltip.Content bg="bg.nav" color="white" fontSize="xs" maxW="320px" p={3} borderRadius="6px">
              {content}
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Portal>
      </Tooltip.Root>
      <VisuallyHidden id={id} data-hover-text="true">
        {content}
      </VisuallyHidden>
    </>
  );
};
